import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Sparkles, PenTool, Calendar, PartyPopper, Star,
  Users, TrendingUp, LogOut, Menu, X, Globe, User, Flame, GraduationCap, Building2, FileSearch, Brain, Target, ArrowUpRight, Home as HomeIcon, BarChart3, MessageCircle,
} from "lucide-react";
import { Logo } from "./Logo";
import { useApp } from "../context/AppContext";
import { useLang, LANGS } from "../lib/i18n";
import { GuidedTour } from "./GuidedTour";
import { JourneyStepper } from "./JourneyStepper";
import { SimpleNav } from "./SimpleNav";
import { useS } from "../lib/simple";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const navItems = (t) => [
  { to: "/app/dashboard", label: t("nav_dashboard"), icon: LayoutDashboard, id: "dashboard" },
  { to: "/app/command", label: t("nav_command"), icon: Sparkles, id: "command" },
  { to: "/app/content", label: t("nav_content"), icon: PenTool, id: "content" },
  { to: "/app/calendar", label: t("nav_calendar"), icon: Calendar, id: "calendar" },
  { to: "/app/event", label: t("nav_event"), icon: PartyPopper, id: "event" },
  { to: "/app/reputation", label: t("nav_reputation"), icon: Star, id: "reputation" },
  { to: "/app/loyalty", label: t("nav_loyalty"), icon: Users, id: "loyalty" },
  { to: "/app/growth", label: t("nav_growth"), icon: TrendingUp, id: "growth" },
  { to: "/app/help", label: t("nav_help"), icon: GraduationCap, id: "help" },
];

function LangSelector({ testid = "lang-selector" }) {
  const { lang, setLang } = useLang();
  const cur = LANGS.find((l) => l.code === lang);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={testid} className="flex items-center justify-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-white transition-colors px-2.5 py-2 min-h-[44px] sm:min-h-0 rounded-lg">
          <Globe size={16} /> <span>{cur?.flag}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="glass border-white/10">
        {LANGS.map((l) => (
          <DropdownMenuItem key={l.code} data-testid={`lang-${l.code}`} onClick={() => setLang(l.code)} className="gap-2 cursor-pointer">
            <span>{l.flag}</span> {l.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Layout({ children }) {
  const { t } = useLang();
  const { s } = useS();
  const { logout, demo, data } = useApp();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const items = navItems(t);
  const homeItem = items[0];
  const journeySteps = [
    { to: "/app/profile-360", id: "profile-360", label: t("nav_profile360"), icon: Building2 },
    { to: "/app/documents", id: "documents", label: t("nav_documents"), icon: FileSearch },
    { to: "/app/diagnostic", id: "diagnostic", label: t("nav_diagnostic"), icon: Brain },
    { to: "/app/plan", id: "plan", label: t("nav_plan"), icon: Target },
  ];
  const PIPELINE_ROUTES = ["/app/profile-360", "/app/documents", "/app/diagnostic", "/app/plan"];
  const showStepper = !demo && PIPELINE_ROUTES.some((r) => pathname.startsWith(r));
  // Mobile bottom-nav: for real users, surface the value pipeline (Diagnostic) instead of Event.
  // Event and all other modules remain accessible via the drawer. Demo keeps its original nav untouched.
  const mobileNavItems = demo
    ? items.slice(0, 5)
    : [
        { to: "/app/dashboard", id: "dashboard", label: s("n_home"), icon: HomeIcon },
        { to: "/app/diagnostic", id: "diagnostic", label: s("mob_point"), icon: Brain },
        { to: "/app/content", id: "content", label: s("mob_clients"), icon: Users },
        { to: "/app/results", id: "results", label: s("mob_results"), icon: BarChart3 },
        { to: "/app/command", id: "command", label: s("mob_assistant"), icon: MessageCircle },
      ];

  const SideContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5"><Logo /></div>
      {demo && (
        <div className="mx-4 mb-3 px-3 py-2 rounded-xl bg-[#00E5FF]/10 border border-[#00E5FF]/20 text-[#00E5FF] text-xs font-semibold text-center space-y-1.5" data-testid="demo-badge">
          <div>{t("demo_mode")} · {data?.onboarding?.business_name || "NOVA COFFEE"}</div>
          <button data-testid="demo-change-activity" onClick={() => nav("/demo")} className="w-full rounded-lg bg-[#00E5FF]/15 hover:bg-[#00E5FF]/25 py-1 transition-colors">{t("demo_change")}</button>
        </div>
      )}
      {!demo ? <SimpleNav onNavigate={() => setOpen(false)} /> : (
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {/* Accueil */}
        <NavLink to={homeItem.to} data-testid={`nav-${homeItem.id}`} onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
              isActive ? "bg-primary/15 text-white border border-primary/30" : "text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-white/5 border border-transparent"
            }`}>
          <homeItem.icon size={18} /> {homeItem.label}
        </NavLink>

        {/* Mon parcours — pipeline principal (Profil 360 → Documents → Diagnostic → Plan) */}
        {!demo && (
          <div className="my-2 rounded-2xl border border-[#00E5FF]/20 bg-[#00E5FF]/[0.04] p-2" data-testid="journey-nav-group">
            <p className="px-2 pt-0.5 pb-1.5 text-[10px] uppercase tracking-wide text-[#00E5FF]/80 font-semibold">{t("journey_title")}</p>
            {journeySteps.map((s, idx) => (
              <NavLink key={s.id} to={s.to} data-testid={`nav-${s.id}`} onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-2.5 py-2.5 rounded-xl text-sm transition-colors ${
                    isActive ? "bg-primary/15 text-white border border-primary/30" : "text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-white/5 border border-transparent"
                  }`}>
                <span className="shrink-0 w-5 h-5 rounded-full bg-white/10 text-[10px] font-bold flex items-center justify-center">{idx + 1}</span>
                <s.icon size={16} className="shrink-0" />
                <span className="truncate">{s.label}</span>
              </NavLink>
            ))}
          </div>
        )}

        {/* Couche d'exécution — Passer à l'action (hiérarchie visuelle, modules inchangés) */}
        <p className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] font-semibold" data-testid="nav-execute-title">{t("nav_execute_title")}</p>
        {items.slice(1, 8).map((it) => (
          <NavLink key={it.id} to={it.to} data-testid={`nav-${it.id}`} onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                isActive ? "bg-primary/15 text-white border border-primary/30" : "text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-white/5 border border-transparent"
              }`}>
            <it.icon size={18} /> {it.label}
          </NavLink>
        ))}
        {/* Aide (hors couche d'exécution) */}
        {items[8] && (
          <NavLink key={items[8].id} to={items[8].to} data-testid={`nav-${items[8].id}`} onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 mt-2 rounded-xl text-sm transition-colors ${
                isActive ? "bg-primary/15 text-white border border-primary/30" : "text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-white/5 border border-transparent"
              }`}>
            {(() => { const HelpIcon = items[8].icon; return <HelpIcon size={18} />; })()} {items[8].label}
          </NavLink>
        )}
      </nav>
      )}
      <div className="p-3 border-t border-white/5 space-y-1">
        {data?.gamification && (
          <div className="flex items-center gap-2 px-3 py-2 text-xs text-[hsl(var(--muted-foreground))]">
            <Flame size={15} className="text-orange-400" /> {data.gamification.streak} jours · {data.gamification.level}
          </div>
        )}
        <NavLink to="/app/profile" data-testid="nav-profile" onClick={() => setOpen(false)}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-white/5">
          <User size={18} /> {t("nav_profile")}
        </NavLink>
        <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[hsl(var(--muted-foreground))] hover:text-red-400 hover:bg-red-500/5">
          <LogOut size={18} /> {t("logout")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[var(--lb-bg)]">
      {/* Living premium background — coherent across internal screens */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 aurora opacity-70" />
        <div className="absolute inset-0 tech-grid opacity-40" />
        <div className="hero-glow-a drift absolute -top-32 -left-20 w-[440px] h-[440px] rounded-full blur-[40px] opacity-25" />
        <div className="hero-glow-b drift2 absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full blur-[40px] opacity-20" />
      </div>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-white/5 glass flex-col fixed inset-y-0 z-40">
        <SideContent />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 glass border-b border-white/5 flex items-center justify-between px-4 h-14">
        <Logo />
        <div className="flex items-center gap-1">
          <a href="https://localboostai.tiiny.site/" target="_blank" rel="noopener noreferrer"
            data-testid="discover-localboost-mobile" title={t("discover_sub")} aria-label={t("discover_cta")}
            className="inline-flex items-center gap-1 rounded-full border border-neon/40 bg-white/5 px-2.5 min-h-[40px] text-xs font-semibold text-white active:bg-neon/10 transition-colors">
            <Sparkles size={13} className="text-neon" /> <ArrowUpRight size={13} className="text-neon" />
          </a>
          <LangSelector testid="lang-selector-mobile" />
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)} className="p-2 -mr-1 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg"><Menu size={22} /></button>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div className="lg:hidden fixed inset-0 bg-black/70 z-40" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} />
            <motion.aside className="lg:hidden fixed inset-y-0 left-0 w-72 z-50 glass border-r border-white/10"
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "spring", damping: 26 }}>
              <button className="absolute top-4 right-4 z-10" onClick={() => setOpen(false)} data-testid="mobile-close"><X size={22} /></button>
              <SideContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main */}
      <main className="relative z-10 flex-1 lg:ml-64 min-w-0">
        <div className="hidden lg:flex justify-end items-center gap-3 px-8 pt-5">
          <a href="https://localboostai.tiiny.site/" target="_blank" rel="noopener noreferrer"
            data-testid="discover-localboost" title={t("discover_sub")}
            className="group inline-flex items-center gap-2 rounded-full border border-neon/40 bg-white/5 hover:bg-neon/10 hover:border-neon px-4 h-10 text-sm font-semibold text-white transition-colors hover:shadow-[0_6px_24px_-6px_rgba(34,211,238,0.5)]">
            <Sparkles size={15} className="text-neon" />
            {t("discover_cta")}
            <ArrowUpRight size={15} className="text-neon transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <LangSelector testid="lang-selector-desktop" />
        </div>
        <div className="px-4 lg:px-8 pt-20 lg:pt-4 pb-24 lg:pb-10">
          {showStepper && <JourneyStepper />}
          <motion.div
            key={typeof window !== "undefined" ? window.location.pathname : "page"}
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: "easeOut" }}>
            {children}
          </motion.div>
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 glass border-t border-white/10 flex justify-around py-2" data-testid="mobile-bottom-nav">
        {mobileNavItems.map((it) => (
          <NavLink key={it.id} to={it.to} data-testid={`mobnav-${it.id}`}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] ${isActive ? "text-neon" : "text-[hsl(var(--muted-foreground))]"}`}>
            <it.icon size={20} /> {demo ? it.label.split(" ")[0] : it.label}
          </NavLink>
        ))}
      </nav>

      <GuidedTour />
    </div>
  );
}
